import { useEffect, useMemo, useState } from 'react';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGridPro, GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';

export interface KpiChecklistModalRow {
  id: string;
  displayName: string;
  referenceCode?: string;
  calculationType?: string;
  groupName?: string;
}

interface KpiChecklistModalProps {
  open: boolean;
  title: string;
  description: string;
  type: 'calculation' | 'attribute';
  rows: KpiChecklistModalRow[];
  loading?: boolean;
  onClose: () => void;
  onAdd: (selectedIds: string[]) => void;
}

const modalContentSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(900px, calc(100vw - 32px))',
  height: 'min(640px, calc(100vh - 32px))',
  bgcolor: 'background.paper',
  borderRadius: 1.5,
  boxShadow: 24,
  overflow: 'hidden',
};

const dataGridSx = {
  borderColor: '#E3EBF6',
  '& .MuiCheckbox-root svg': {
    color: '#98A2AE',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#EDF2FF',
    borderBottom: '1px solid #E3EBF6',
  },
};

export function KpiChecklistModal({
  open,
  title,
  description,
  type,
  rows,
  loading = false,
  onClose,
  onAdd,
}: KpiChecklistModalProps) {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItems, setSelectedItems] = useState<GridSelectionModel>([]);
  const titleId = `kpi-${type}-checklist-modal-title`;

  useEffect(() => {
    if (!open) return;

    setSearchKeyword('');
    setSelectedItems([]);
  }, [open]);

  const columns = useMemo<GridColDef[]>(
    () =>
      type === 'calculation'
        ? [
            { field: 'displayName', headerName: 'Display Name', flex: 1, minWidth: 180 },
            { field: 'calculationType', headerName: 'Type', flex: 0.8, minWidth: 150 },
            { field: 'groupName', headerName: 'Group', flex: 0.8, minWidth: 150 },
          ]
        : [
            { field: 'displayName', headerName: 'Display Name', flex: 1, minWidth: 180 },
            { field: 'referenceCode', headerName: 'Reference Code', flex: 0.7, minWidth: 140 },
          ],
    [type]
  );

  const filteredRows = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return rows;

    return rows.filter((row) =>
      Object.values(row).some((value) => value?.toString().toLowerCase().includes(keyword))
    );
  }, [rows, searchKeyword]);

  const handleAdd = () => {
    onAdd(selectedItems.map(String));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={titleId}>
      <Stack sx={modalContentSx}>
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' px={2.5} py={2}>
          <Box>
            <Typography id={titleId} variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
              {title}
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.25}>
              {description}
            </Typography>
          </Box>
          <IconButton size='small' aria-label='Close' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>

        <Stack bgcolor='#FBFBFB' px={2.5} py={2} borderTop='1px solid #E3EBF6'>
          <TextField
            value={searchKeyword}
            onChange={(event) => setSearchKeyword(event.target.value)}
            size='small'
            placeholder='Search available items...'
            inputProps={{ 'aria-label': 'Search available items' }}
          />
        </Stack>

        <Box flex={1} minHeight={0} px={2.5} py={2}>
          <DataGridPro
            sx={dataGridSx}
            rows={filteredRows}
            columns={columns}
            loading={loading}
            density='compact'
            checkboxSelection
            disableSelectionOnClick
            keepNonExistentRowsSelected
            selectionModel={selectedItems}
            onSelectionModelChange={setSelectedItems}
            pagination
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            localeText={{ noRowsLabel: 'No available items' }}
          />
        </Box>

        <Box px={2.5} pb={1.5}>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
            <Typography variant='body2' color='text.secondary'>
              {selectedItems.length} selected
            </Typography>
            <Stack direction='row' spacing={1.5}>
              <Button variant='main-table-panel-border' onClick={onClose}>
                Cancel
              </Button>
              <Button variant='main-table-panel' disabled={selectedItems.length === 0} onClick={handleAdd}>
                Add selected
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
